import { useCallback, useMemo } from 'react';
import { FieldErrors, FieldValues, useFormContext } from 'react-hook-form';
import { formatFieldLabel } from '../validation/validationUtils';

interface FormErrorListProps {
  className?: string;
  indexForArray?: number;
}

const isGlobalError = (input: any) => {
  return input.type && input.message;
};

export const FormErrorList = <T extends FieldValues>({ className, indexForArray }: FormErrorListProps) => {
  const { formState } = useFormContext<T>();

  const fieldErrorsToErrorList = useCallback((fieldErrors: FieldErrors<T>, arrayIndex?: number, parentPath = ''): string[] => {
    if (fieldErrors == null) return [];
    const entries = Object.entries(fieldErrors);
    if (entries.length === 0) return [];
    const errors = [];

    //siteList being outlined here as its a special case where errors across forms were being smushed together.
    if (entries[0][0] === 'siteList' && arrayIndex != undefined) {
      const relatedErrors = entries[0][1] as Record<string, any>;
      errors.push(...fieldErrorsToErrorList(relatedErrors[arrayIndex]));
    } else {
      for (const [key, error] of entries) {
        if (!error) continue;

        const currentPath = parentPath ? `${parentPath}.${key}` : key;

        if (isGlobalError(error)) {
          if (key === 'root') {
            // if there's a validation error with an array get it's message
            errors.push(error.message as string);
            continue;
          }

          errors.push(formatFieldLabel(currentPath));
          continue;
        }
        errors.push(...fieldErrorsToErrorList(error as any));
      }
    }
    return errors;
  }, []);

  const errors = useMemo(() => {
    return fieldErrorsToErrorList(formState.errors, indexForArray);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldErrorsToErrorList, formState.errors]);

  if (!errors || errors.length === 0) return null;

  return (
    <div className={`pb-2 text-feedback-red ${className}`}>
      {`There are errors with the following fields: `}
      <ul className="list-disc pl-4">
        {errors.map((error, index) => (
          <li key={index}>{error}</li>
        ))}
      </ul>
    </div>
  );
};
